import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Error, ErrorOutline } from '@mui/icons-material';
import { Divider, List, Menu, MenuItem } from '@mui/material';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { Box, ListItem } from '/src/components';
import { useAlertStore } from '/src/context';
import { DataModelPluralType } from '/src/lib/types';

export const AlertsMenu: React.FC = observer(() => {
  /** Context **/
  const alertStore = useAlertStore();

  /** State **/
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  /** Computed **/
  const open = !!anchorEl;
  const alerts = useMemo(
    () => alertStore.alerts?.toSorted((a, b) => (dayjs(b.created).isAfter(a.created) ? 1 : -1)).slice(0, 10),
    [alertStore.alerts]
  );
  const alertCount = useMemo(() => alertStore.newAlertCount, [alertStore.newAlertCount]);

  /** Methods **/

  const handleClick = (event: React.MouseEvent<any>) => {
    setAnchorEl(event.currentTarget);
    alertStore.markAsRead();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  /** Render **/

  return (
    <List component="ul" className="float-right">
      <ListItem>
        <Box
          component="a"
          id="alerts-button"
          aria-label="Alerts"
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          layout-align="center center"
          lineHeight="64px"
          height="64px"
          className="pad-sides"
        >
          {!!alertCount && <Error className="md-icon material-icons pad-right-xs" />}
          {!alertCount && <ErrorOutline className="md-icon material-icons" />}
          {!!alertCount && (
            <Box className="alert-badge new" layout-align="center center">
              <Box component="span" className="medium">
                {alertCount}
              </Box>
            </Box>
          )}
        </Box>

        <Menu
          className="md-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          slotProps={{
            list: {
              'aria-labelledby': 'alerts-button',
            },
          }}
          md-position-mode="target-left target"
        >
          <Box className={'md-menu-content[width="4"]'}>
            {alerts?.map((alert) => (
              <MenuItem key={alert._id} className="alerts-menu-item">
                <Link
                  className="md-button md-default-theme"
                  to={`/${alert.source?.__t}/${alert.source?._id}`}
                  aria-label={alert.name}
                >
                  {(alert.source?.__t === DataModelPluralType.Payment.toLocaleLowerCase() ||
                    alert.source?.__t === DataModelPluralType.Advance.toLocaleLowerCase()) && (
                    <Box layout-align="start center" />
                  )}
                  <Box component="span">{alert.message}</Box>
                </Link>
              </MenuItem>
            ))}
          </Box>

          {!!alerts?.length && <Divider />}

          <MenuItem>
            <Box sx={{ flex: 1 }} layout-align="center center">
              {!!alerts?.length && (
                <Link to="/alerts" className="pad-top-bottom">
                  View All Alerts...
                </Link>
              )}
              {!alerts?.length && <Box>No Alerts Found.</Box>}
            </Box>
          </MenuItem>
        </Menu>
      </ListItem>
    </List>
  );
});
